/* global WtoyGacha, jQuery */
import { isHoldBlockedResponse, fetchHold, mountDrawHoldPopup, showShipHoldPrompt } from './lib/draw-hold.mjs';
import { createGachaDrawApi } from './lib/gacha-draw-api.mjs';
import { drawBarControls, readLimitState } from './lib/gacha-draw-limits.mjs';
import { shouldRefreshAfterReveal } from './lib/gacha-reveal-refresh.mjs';
import { createRestClient } from './lib/rest-client.mjs';
import { createDrawPayPopup } from './lib/draw-pay-popup.js';
import { initialState as initialDrawFlowState, reduce as reduceDrawFlow } from './lib/draw-flow.mjs';
import { dedupeByClientId, countUniqueClients } from './lib/presence-members.mjs';
import { renderableClientIds, fetchDollCards, onlineDollsHtml } from './lib/presence-dolls.mjs';

(function ($) {
    'use strict';

    var cfg              = WtoyGacha || {};
    var restBase         = cfg.rest_base    || '';
    var nonce            = cfg.nonce        || '';
    var productId        = cfg.product_id   || 0;
    var pricePerUnit     = cfg.pricePerUnit || [0, 0, 0]; // [price1, price5, price10]
    var gachaProductType = cfg.gacha_product_type || 'virtual';
    var userToken        = cfg.user_token || '';
    var readyToShipLink  = cfg.ready_to_ship_link || '';
    var drawHoldPopup    = null;

    // 轉蛋前台已全數 REST 化，一把 wp_rest nonce 走完全站
    var restClient = createRestClient({ restBase: restBase, nonce: nonce });
    // /gacha/winners 是匿名可讀的公開端點：帶 nonce 反而會被
    // rest_cookie_check_errors() 檢查，nonce 過期（page cache／分頁開超過 12h）
    // 時匿名訪客會被判 403，靜默停掉自動刷新。公開端點一律不帶 nonce。
    var publicRestClient = createRestClient({ restBase: restBase, nonce: '' });

    // ── 抽獎付款彈窗（與一番賞共用 js/src/lib/draw-pay-popup.js）───────────
    // 轉蛋沒有選號盤面，drawFlow.selection 恆為 []；抽獎次數另用 pendingDraws
    // 記錄，供報價／確認抽獎／付現預留共用同一個數字。
    var drawFlow     = initialDrawFlowState();
    var pendingDraws = 0;
    var gachaApi     = createGachaDrawApi({
        restBase: restBase,
        nonce:    nonce,
    });

    // 付款回程與當頁刷卡立即成功的結果只存純資料，不像純點數立即成功那樣附帶
    // inventory_html 與 eligibility_update（見 GachaDrawResultPayload），揭曉後得另外
    // 補打 /gacha/doll/inventory 重抓背包、/gacha/eligibility 重抓每日限購。
    // 純點數立即成功已經用回應內建的兩者更新過，不必再多打一次。
    var pendingRevealRefresh = false;

    // 抽獎列上限的目前狀態（限量剩餘配額、每日限購剩餘、是否全面停用），
    // 初值來自 gacha-draw-bar.php 輸出的 data-limit-* 屬性
    var drawBarEl  = document.querySelector('.wgp-draw-bar');
    var drawLimits = readLimitState(drawBarEl ? drawBarEl.dataset : {});

    function dispatchDrawFlow(event) {
        if (shouldRefreshAfterReveal(event)) {
            pendingRevealRefresh = true;
        }
        var result = reduceDrawFlow(drawFlow, event);
        drawFlow = result.state;
        result.effects.forEach(applyDrawFlowEffect);
        return drawFlow;
    }

    function refetchInventory() {
        restClient.get('/gacha/doll/inventory', { product_id: productId }).then(function (res) {
            if (res.success) {
                updateInventory(res.data.inventory_html, res.data.inventory_count);
            }
        });
    }

    function refetchEligibility() {
        gachaApi.eligibility(productId).then(function (res) {
            if (res.success && res.data.eligibility_update) {
                updateEligibilityDisplay(res.data.eligibility_update);
            }
        });
    }

    function applyDrawFlowEffect(effect) {
        if (effect.type === 'redirect') {
            location.href = effect.url;
            return;
        }
        var handlers = {
            setSubmitDisabled:    function (disabled) { $('#wtoy_draw_pay_confirm').prop('disabled', disabled); },
            closeDrawPayPopup:    function () { $('#wtoy_draw_pay_popup').removeClass('open'); },
            renderDrawPayPopup:   drawPayPopup.renderDrawPayPopup,
            requestQuote:         requestQuote,
            setDrawReturnMessage: drawPayPopup.setDrawReturnMessage,
            clearDrawReturnState: drawPayPopup.clearDrawReturnState,
            markReturnTerminal:   drawPayPopup.markReturnTerminal,
            // 轉蛋沒有籤紙揭曉（無 Peel），hasPeel 恆 false，reveal 一律走
            // 'overview' 模式；本頁的結果彈窗是既有的 showDrawResult()，不是
            // 一番賞那份總覽 markup，兩者不共用
            renderOverviewPopup:  handleDrawReveal,
            renderGroupBuyPopup:  handleDrawReveal,
        };
        var handler = handlers[effect.name];
        if (handler) {
            handler.apply(null, effect.args);
        }
    }

    // reveal.result 原樣透傳（見 draw-flow.mjs）：純點數立即成功給的是
    // res.data.results（陣列），付現回程／當頁刷卡走 GachaDrawResultPayload
    // 給的是 {results, is_limited}（物件），兩種形狀都可能出現，這裡統一拆開。
    function handleDrawReveal(result) {
        var results = (result && result.results) ? result.results : result;
        showDrawResult(results);
        if (pendingRevealRefresh) {
            pendingRevealRefresh = false;
            refetchInventory();
            // 頁面沒有每日限購條件時不打
            if (drawLimits.dailyRemaining !== null) {
                refetchEligibility();
            }
        }
    }

    // 折抵勾選去抖動：連按時只送最後一次報價
    var DISCOUNT_TOGGLE_DEBOUNCE_MS = 275;
    var discountToggleTimer = null;

    function requestQuote(flags, seq) {
        clearTimeout(discountToggleTimer);
        discountToggleTimer = setTimeout(function () {
            gachaApi.quote(productId, pendingDraws, flags).then(function (res) {
                if (!res.success) {
                    alert(res.data && res.data.message);
                    dispatchDrawFlow({ type: 'quoteFailed', seq: seq });
                    return;
                }
                dispatchDrawFlow({ type: 'quoteLoaded', quote: res.data, seq: seq });
            }, function () {
                alert('系統異常，請稍後再試。');
                dispatchDrawFlow({ type: 'quoteFailed', seq: seq });
            });
        }, DISCOUNT_TOGGLE_DEBOUNCE_MS);
    }

    function openDrawPayPopup(draws) {
        pendingDraws = draws;
        // 每次開窗都是全開：勾選狀態不跨次記憶
        drawPayPopup.resetPreferenceApplied();
        return gachaApi.quote(productId, draws).then(function (res) {
            if (!res.success) {
                alert((res.data && res.data.message) || '報價失敗，請稍後再試');
                return;
            }
            $('#wtoy_draw_pay_agree').prop('checked', false);
            dispatchDrawFlow({ type: 'quoteLoaded', quote: res.data });
            $('#wtoy_draw_pay_popup').addClass('open');
        }, function () {
            alert('系統異常，請稍後再試');
        });
    }

    function selectedGatewayRedirectMode(gatewayId) {
        var gateway = (drawFlow.quote && drawFlow.quote.gateways || []).filter(function (gw) {
            return gw.id === gatewayId;
        })[0];
        return gateway ? gateway.redirect_mode : null;
    }

    function applyDrawSideEffects(data) {
        updateInventory(data.inventory_html, data.inventory_count);
        if (cfg.gacha_mode === 'limited' && data.results) {
            updateLimitedQuotaDisplay(data.results);
        }
        if (data.eligibility_update) {
            updateEligibilityDisplay(data.eligibility_update);
        }
    }

    function confirmDraw() {
        if (drawFlow.quote && drawFlow.quote.cash_amount > 0) {
            var gatewayId = $('input[name=wtoy_draw_pay_gateway]:checked').val();
            if (selectedGatewayRedirectMode(gatewayId) === 'in_popup') {
                drawPayPopup.confirmCardDraw(gatewayId);
            } else {
                drawPayPopup.confirmReserve();
            }
            return;
        }

        var billingEmail = drawPayPopup.requireNotifyEmail();
        if (billingEmail === null) {
            return;
        }

        var afterSubmit = dispatchDrawFlow({ type: 'submitPointsOnly' });
        if (afterSubmit.phase !== 'submitting') {
            return;
        }

        gachaApi.pointsOnlyDraw(productId, pendingDraws, drawFlow.discountFlags, billingEmail).then(function (res) {
            if (!res.success) {
                if (!handleDrawBlockedResponse(res)) {
                    alert((res.data && res.data.message) || '抽獎失敗，請稍後再試');
                }
                dispatchDrawFlow({ type: 'submitBlocked' });
                return;
            }
            applyDrawSideEffects(res.data);
            dispatchDrawFlow({ type: 'resultReady', result: res.data.results, hasPeel: false, fromPointsOnly: true });
        }, function () {
            alert('連線錯誤，請稍後再試');
            dispatchDrawFlow({ type: 'submitBlocked' });
        });
    }

    var drawPayPopup = createDrawPayPopup({
        $:                     $,
        drawApi:               gachaApi,
        getProductId:          function () { return productId; },
        getSelection:          function () { return []; },
        getExtraPayload:       function () { return { draws: pendingDraws }; },
        getDiscountFlags:      function () { return drawFlow.discountFlags; },
        getFlow:               function () { return { quote: drawFlow.quote, quoting: drawFlow.quoting }; },
        dispatch:              dispatchDrawFlow,
        hasPeel:               function () { return false; },
        handleBlockedResponse: function (res) { return handleDrawBlockedResponse(res); },
        devToolsEnabled:       false,
    });

    $(document.body).on('click', '[data-wtoy-close-draw-pay]', function () {
        $('#wtoy_draw_pay_popup').removeClass('open');
    });

    $(document.body).on('click', '#wtoy_draw_pay_confirm', confirmDraw);

    /**
     * 被擋的回應若帶 ship_hold 碼，顯示伺服器訊息並問要不要前往出貨頁處理；帶 draw_hold 碼
     * 改開三處共用彈窗。兩者都不是才交回呼叫端 alert()。
     * @param {{success: boolean, data?: {message?: string, code?: string}}} res
     * @returns {boolean} true = 已處理，呼叫端不必再 alert()
     */
    function handleDrawBlockedResponse(res) {
        if (showShipHoldPrompt(res.data, readyToShipLink)) {
            return true;
        }

        if (!drawHoldPopup || !isHoldBlockedResponse(res.data)) {
            return false;
        }

        fetchHold(restBase, nonce).then(function (result) {
            drawHoldPopup.open(result.hold, result.serverTime, result.sentAt, result.receivedAt);
        });
        return true;
    }

    function calculateCheapestPrice(quantity) {
        var total = 0;
        while (quantity > 0) {
            if (quantity >= 10)     { total += 10 * pricePerUnit[2]; quantity -= 10; }
            else if (quantity >= 5) { total += 5 * pricePerUnit[1]; quantity -= 5;  }
            else                    { total += quantity * pricePerUnit[0]; quantity = 0; }
        }
        return total;
    }

    // ── 稀有度顏色 ──────────────────────────────────────────
    var rarityColors = { 4: '#311b92', 3: '#7c4dff', 2: '#ffd600', 1: '#9e9e9e' };
    var rarityLabels = { 4: '黑鑽款', 3: '鑽石款', 2: '黃金款', 1: '白銀款' };

    // ── 獎項詳情彈窗 ─────────────────────────────────────────
    $(document).on('click', '.wgp-prize-clickable', function (e) {
        e.stopPropagation();
        var $card = $(this);
        var name        = $card.data('prize-name') || '';
        var description = $card.data('description') || '';
        var rarity      = parseInt($card.data('rarity'), 10) || 1;
        var rarityLabel = $card.data('rarity-label') || '';
        var returnPts   = parseInt($card.data('return-points'), 10) || 0;
        var bossRate    = parseInt($card.data('boss-rate'), 10) || 0;
        var weight      = parseInt($card.data('weight'), 10) || 0;
        var rarityTotalW = parseInt($card.data('rarity-total-weight'), 10) || 0;
        var rarityCount = parseInt($card.data('rarity-count'), 10) || 0;
        var imgUrl      = $card.data('img-url') || '';
        var shippingTime = $card.data('shipping-time') || '';
        var isLimited   = parseInt($card.data('is-limited'), 10) === 1;
        var color       = rarityColors[rarity] || '#9e9e9e';

        var imgHtml = imgUrl
            ? `<img src="${escAttr(imgUrl)}" alt="${escAttr(name)}">`
            : '<div class="wgp-detail-no-img">🎁</div>';

        var html;
        if (isLimited) {
            var quota          = parseInt($card.data('quota'), 10) || 0;
            var remaining      = parseInt($card.data('remaining'), 10) || 0;
            var totalRemaining = parseInt($card.data('total-remaining'), 10) || 0;
            var pricePerDraw   = parseInt($card.data('price-per-draw'), 10) || 0;
            var currentProb    = totalRemaining > 0
                ? (Math.floor((remaining / totalRemaining * 100) * 10000) / 10000).toString()
                : '0';

            var infoHtml = ''
                + `<div class="wgp-ldetail-info-cell"><span class="wgp-ldetail-info-label">稀有度</span><span class="wgp-ldetail-info-val wgp-ldetail-val-rarity">${escHtml(rarityLabel)}</span></div>`
                + `<div class="wgp-ldetail-info-cell"><span class="wgp-ldetail-info-label">價值</span><span class="wgp-ldetail-info-val wgp-ldetail-val-accent">${returnPts.toLocaleString()} 點</span></div>`
                + `<div class="wgp-ldetail-info-cell"><span class="wgp-ldetail-info-label">剩餘 / 總量</span><span class="wgp-ldetail-info-val">${remaining} / ${quota}</span></div>`
                + `<div class="wgp-ldetail-info-cell"><span class="wgp-ldetail-info-label">當前抽中機率</span><span class="wgp-ldetail-info-val">${currentProb}%</span></div>`;

            var infoSvg  = `<svg class="wgp-ldetail-section-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`;
            var truckSvg = `<svg class="wgp-ldetail-section-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>`;

            var sectionsHtml = ''
                + `<div class="wgp-ldetail-section">`
                +   `<div class="wgp-ldetail-section-title">${infoSvg}商品說明</div>`
                +   `<div class="wgp-ldetail-section-body">${escHtml(description || '暫無說明')}</div>`
                + `</div>`
                + `<div class="wgp-ldetail-section">`
                +   `<div class="wgp-ldetail-section-title">${truckSvg}出貨時間</div>`
                +   `<div class="wgp-ldetail-section-body">${escHtml(shippingTime || '中獎後 7-14 個工作天內出貨')}</div>`
                + `</div>`;

            var closeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

            html =
                `<div class="wgp-prize-detail-overlay wgp-prize-detail-overlay--limited">`
                + `<div class="wgp-prize-detail-modal wgp-prize-detail-modal--limited" data-rarity="${rarity}">`
                +   `<h2 class="wgp-ldetail-title">${escHtml(name)}</h2>`
                +   `<div class="wgp-ldetail-frame">`
                +     `<div class="wgp-ldetail-inner">`
                +       `<div class="wgp-ldetail-image">${imgHtml}</div>`
                +       `<div class="wgp-ldetail-info-grid">${infoHtml}</div>`
                +       `<div class="wgp-ldetail-sections">${sectionsHtml}</div>`
                +       `<div class="wgp-ldetail-footer">每抽 ${pricePerDraw} 點</div>`
                +     `</div>`
                +   `</div>`
                +   `<button class="wgp-detail-close" type="button" aria-label="關閉">${closeSvg}</button>`
                + `</div>`
                + `</div>`;
        } else {
            var heroBgColors = { 4: 'rgba(26,26,46,0.10)', 3: 'rgba(124,77,255,0.08)', 2: 'rgba(255,214,0,0.12)', 1: 'rgba(158,158,158,0.08)' };
            var heroBg      = heroBgColors[rarity] || '#f7f7f7';

            // 機率計算
            var probPct = parseFloat((weight / 1000).toFixed(3));
            var rarityProbPct = parseFloat((rarityTotalW / 1000).toFixed(3));

            // 組裝 hero 區（圖 + badge + 名稱 + 市值 + 收益，全部置中）
            var heroHtml = '';
            if (returnPts > 0) {
                heroHtml += `<div class="wgp-detail-hero-pts">市值 ${returnPts.toLocaleString()} 玩瘋券</div>`;
            }
            if (bossRate > 0) {
                heroHtml += `<div class="wgp-detail-hero-boss">幕後主宰收益 ${bossRate / 100}%</div>`;
            }

            var bodyHtml = '';
            if (description) {
                bodyHtml += `<div class="wgp-detail-desc">`
                    + `<div class="wgp-detail-desc-title">🎁 商品介紹</div>`
                    + `<div class="wgp-detail-desc-body">${escHtml(description)}</div>`
                    + `</div>`;
            }
            bodyHtml += `<div class="wgp-detail-prob">`
                + `<div class="wgp-detail-prob-title">🔢 單一商品中獎機率</div>`
                + `<div class="wgp-detail-prob-text">所屬分類（${escHtml(rarityLabel)}）總機率：<span class="wgp-detail-prob-val">${rarityProbPct}%</span></div>`
                + `<div class="wgp-detail-prob-text">該分類共 <span class="wgp-detail-prob-val">${rarityCount}</span> 件商品</div>`
                + `<div class="wgp-detail-prob-row wgp-detail-prob-highlight">`
                +   `<span>本商品中獎機率：</span>`
                +   `<span>${probPct}%</span>`
                + `</div>`
                + `</div>`;
            if (shippingTime) {
                bodyHtml += `<div class="wgp-detail-shipping">`
                    + `<div class="wgp-detail-shipping-title">🚚 出貨時間</div>`
                    + `<div class="wgp-detail-shipping-text">${escHtml(shippingTime)}</div>`
                    + `</div>`;
            }

            html =
                `<div class="wgp-prize-detail-overlay">`
                + `<div class="wgp-prize-detail-modal">`
                +   `<button class="wgp-detail-close" type="button">&times;</button>`
                +   `<div class="wgp-detail-hero" style="background:${heroBg}">`
                +     `<div class="wgp-detail-img-wrap">${imgHtml}</div>`
                +     `<div class="wgp-detail-badge" style="background:${escAttr(color)}">${escHtml(rarityLabel)}</div>`
                +     `<div class="wgp-detail-name">${escHtml(name)}</div>`
                +     heroHtml
                +   `</div>`
                +   `<div class="wgp-detail-body">${bodyHtml}</div>`
                +   `<button class="wgp-detail-back" type="button">返回</button>`
                + `</div>`
                + `</div>`;
        }

        var $overlay = $(html).appendTo('body');
        $overlay.on('click', '.wgp-detail-close, .wgp-detail-back', function () { $overlay.remove(); });
        $overlay.on('click', function (ev) {
            if ($(ev.target).is('.wgp-prize-detail-overlay')) { $overlay.remove(); }
        });
    });

    // ── Phase 4：機率展開收合 ───────────────────────────────
    $(document).on('click', '.wgp-rarity-header', function () {
        var $header = $(this);
        var $grid   = $header.next('.wgp-prize-grid');
        var $arrow  = $header.find('.wgp-arrow');
        $grid.slideToggle(200);
        $arrow.toggleClass('open');
    });

    // ── 背包收合 ───────────────────────────────────────────
    $(document).on('click', '.wgp-backpack-header', function (e) {
        // 不攔截 actions 區域的按鈕點擊（退回模式、一鍵退回）
        if ($(e.target).closest('.wgp-backpack-actions').length && !$(e.target).closest('.wgp-backpack-toggle').length) return;
        var $backpack = $(this).closest('.wgp-backpack');
        var $grid   = $backpack.find('.wgp-inventory-grid');
        var $toggle = $(this).find('.wgp-backpack-toggle');
        $grid.slideToggle(200, function () {
            $toggle.text($grid.is(':visible') ? '收起' : '展開');
        });
    });

    // ── 篩選列折疊 ────────────────────────────────────────
    $(document).on('click', '.wgp-filter-toggle-btn', function (e) {
        e.stopPropagation();
        var $backpack = $(this).closest('.wgp-backpack');
        var $filters  = $backpack.find('.wgp-backpack-filters');
        $filters.slideToggle(200);
        $(this).toggleClass('active');
    });

    // ── 背包篩選 ─────────────────────────────────────────
    function applyBackpackFilter($filters) {
        var slot    = $filters.find('.wtoy-filter-slot').val();
        var rarity  = $filters.find('.wtoy-filter-rarity').val();
        var keyword = $filters.find('.wtoy-filter-name').val().trim().toLowerCase();
        $filters.siblings('.wgp-inventory-grid').find('.wgp-inventory-item').each(function () {
            var name = $(this).find('.wgp-inv-name').text().toLowerCase();
            var show = (!slot || $(this).data('slot') === slot)
                    && (!rarity || String($(this).data('rarity')) === rarity)
                    && (!keyword || name.indexOf(keyword) !== -1);
            $(this).toggle(show);
        });
        // 篩選後取消隱藏項目的勾選
        $filters.siblings('.wgp-inventory-grid')
            .find('.wgp-inventory-item:hidden.wgp-return-checked')
            .removeClass('wgp-return-checked');
        updateBatchBar();
    }
    $(document).on('change', '.wgp-backpack-filters .wtoy-filter-slot, .wgp-backpack-filters .wtoy-filter-rarity', function () {
        applyBackpackFilter($(this).closest('.wgp-backpack-filters'));
    });
    $(document).on('input', '.wgp-backpack-filters .wtoy-filter-name', function () {
        applyBackpackFilter($(this).closest('.wgp-backpack-filters'));
    });

    // ── Phase 2：快抽按鈕 ─────────────────────────────────
    $(document).on('click', '.wgp-draw-btn', function () {
        var draws = parseInt($(this).data('draws'), 10);
        openDrawPayPopup(draws);
    });

    // ── 自訂數量：即時計算最便宜費用 ───────────────────────
    function updateCustomCost() {
        var draws = parseInt($('.wgp-draw-qty').val(), 10);
        var $value = $('.wgp-custom-cost-value');
        var $badge = $('.wgp-custom-cost-badge');
        if (isNaN(draws) || draws < 1) {
            $value.text('');
            $badge.css('visibility', 'hidden');
        } else {
            $value.text(calculateCheapestPrice(draws).toLocaleString());
            $badge.css('visibility', 'visible');
        }
    }
    $(document).on('input change', '.wgp-draw-qty', updateCustomCost);
    updateCustomCost();

    // ── +/- 按鈕 ─────────────────────────────────────────────
    $(document).on('click', '.wgp-qty-minus, .wgp-qty-plus', function () {
        var $input = $('.wgp-draw-qty');
        var val = parseInt($input.val(), 10) || 1;
        var min = parseInt($input.attr('min'), 10) || 1;
        var max = parseInt($input.attr('max'), 10) || 999;
        if ($(this).hasClass('wgp-qty-minus')) {
            val = Math.max(min, val - 1);
        } else {
            val = Math.min(max, val + 1);
        }
        $input.val(val).trigger('change');
    });

    // ── 自訂數量抽獎 ───────────────────────────────────────
    $(document).on('click', '.wgp-draw-custom-btn', function () {
        var draws = parseInt($('.wgp-draw-qty').val(), 10);
        if (isNaN(draws) || draws < 1) {
            alert('請輸入有效的抽獎次數');
            return;
        }
        openDrawPayPopup(draws);
    });

    // ── 限定模式：點擊 wgp-draw-custom 任何位置（除 qty 控制與 btn 本身）觸發自訂抽 ──
    $(document).on('click', '.wgp-draw-bar--limited .wgp-draw-custom', function (e) {
        if ($(e.target).closest('.wgp-qty-wrap, .wgp-draw-custom-btn').length) return;
        var $btn = $(this).find('.wgp-draw-custom-btn');
        if ($btn.prop('disabled')) return;
        $btn.trigger('click');
    });

    // ── 顯示抽獎結果彈窗 ────────────────────────────────────
    function showDrawResult(results) {
        var rarityIcons  = { 4: '✦', 3: '✦', 2: '🎁', 1: '🎁' };
        var rarityBg     = { 4: '#1a1a2e', 3: '#1565c0', 2: '#ffbf01', 1: '#e0e0e0' };
        var rarityFg     = { 4: '#fff',    3: '#fff',    2: '#fff',    1: '#555'    };
        var rarityRowBg  = { 4: 'rgb(233, 213, 255)', 3: 'rgba(21,101,192,0.06)', 2: 'rgba(255,191,1,0.12)', 1: 'rgba(200,200,200,0.18)' };

        var totalPoints = 0;
        var listHtml = '';
        results.forEach(function (item) {
            var icon   = rarityIcons[item.rarity]  || '🎁';
            var bg     = rarityBg[item.rarity]     || '#e0e0e0';
            var fg     = rarityFg[item.rarity]     || '#555';
            var rowBg  = rarityRowBg[item.rarity]  || 'transparent';
            var pts   = item.return_points || 0;
            totalPoints += pts;
            var ptsHtml = pts > 0 ? `<span class="wgp-result-row-pts">${pts.toLocaleString()} 玩瘋券</span>` : '';
            var iconContent = item.prize_image_url
                ? `<img src="${escHtml(item.prize_image_url)}" alt="">`
                : `<span>${icon}</span>`;
            var iconStyle = item.prize_image_url ? '' : `background:${bg};color:${fg};`;
            listHtml +=
                `<div class="wgp-result-row" style="background:${rowBg}">`
                + `<div class="wgp-result-row-icon" style="${iconStyle}">${iconContent}</div>`
                + `<div class="wgp-result-row-info">`
                +   `<span class="wgp-result-row-name">${escHtml(item.prize_name)}</span>`
                +   `<span class="wgp-result-row-rarity">${escHtml(item.rarity_label || '')}</span>`
                + `</div>`
                + ptsHtml
                + `</div>`;
        });

        var html =
            `<div class="wgp-modal-overlay">`
            + `<div class="wgp-modal wgp-modal--result">`
            +   `<div class="wgp-modal-header">`
            +     `<button class="wgp-modal-x" type="button">✕</button>`
            +     `<div class="wgp-modal-hero">🎉</div>`
            +     `<p class="wgp-modal-title">🎊 恭喜獲得稀有獎品！</p>`
            +   `</div>`
            +   `<div class="wgp-result-list">${listHtml}</div>`
            +   `<div class="wgp-modal-footer">`
            +     `<div class="wgp-modal-total"><span>總獲得價值</span><span class="wgp-modal-total-pts">${totalPoints.toLocaleString()} 玩瘋券</span></div>`
            +     `<button class="wgp-modal-close" type="button">確認</button>`
            +   `</div>`
            + `</div>`
            + `</div>`;

        var $overlay = $(html).appendTo('body');
        $overlay.on('click', '.wgp-modal-close, .wgp-modal-x', function () {
            $overlay.remove();
            dispatchDrawFlow({ type: 'revealFinish' });
        });
        $overlay.on('click', function (e) {
            if ($(e.target).is('.wgp-modal-overlay')) {
                $overlay.remove();
                dispatchDrawFlow({ type: 'revealFinish' });
            }
        });
    }

    // ── 更新資格限制面板數字 ────────────────────────────────
    function updateEligibilityDisplay(update) {
        if (update.spending !== undefined) {
            var $item = $('[data-condition="spending"]');
            $item.find('.wgp-elig-current').text(Number(update.spending.current).toLocaleString());
            $item.toggleClass('is-passed', !!update.spending.passed)
                 .toggleClass('is-failed', !update.spending.passed);
        }
        if (update.daily_limit !== undefined) {
            var $item = $('[data-condition="daily_limit"]');
            $item.find('.wgp-elig-remaining').text(update.daily_limit.remaining);
            $item.toggleClass('is-passed', !!update.daily_limit.passed)
                 .toggleClass('is-failed', !update.daily_limit.passed);
            // 配額用完時隱藏抽獎按鈕
            var $bar = $('.wgp-draw-bar');
            if (!update.daily_limit.passed) {
                $bar.addClass('is-ineligible');
            } else {
                $bar.removeClass('is-ineligible');
            }
            drawLimits.dailyRemaining = parseInt(update.daily_limit.remaining, 10) || 0;
            applyDrawLimits();
        }
    }

    // ── 抽獎列上限：1/5/10 抽、自訂數量 max、自訂抽按鈕 ────────────────
    // 規則同首屏的 GachaDrawLimits::cap()（見 gacha-draw-limits.mjs），
    // 限量配額推播、每日限購更新、下架都經這裡重算
    function applyDrawLimits() {
        var $qty = $('.wgp-draw-qty');
        var $btns = $('.wgp-draw-btn');
        var controls = drawBarControls({
            drawMax:        parseInt($qty.attr('data-draw-max'), 10) || 999,
            quotaRemaining: drawLimits.quotaRemaining,
            dailyRemaining: drawLimits.dailyRemaining,
            blocked:        drawLimits.blocked,
            bundles:        $btns.map(function () { return parseInt($(this).data('draws'), 10) || 0; }).get(),
        });

        $btns.each(function () {
            var n = parseInt($(this).data('draws'), 10) || 0;
            $(this).prop('disabled', !!controls.bundleDisabled[n]);
        });
        if ($qty.length) {
            $qty.attr('max', controls.qtyMax);
            var cur = parseInt($qty.val(), 10) || 1;
            if (cur > controls.qtyMax) {
                $qty.val(controls.qtyMax).trigger('change');
            }
        }
        $('.wgp-draw-custom-btn').prop('disabled', controls.customDisabled);
    }

    // ── Phase 4：更新背包 DOM ───────────────────────────────
    function updateInventory(html, count) {
        if (typeof html === 'undefined') {
            return;
        }
        $('.wgp-inventory-grid').html(html);
        $('.wgp-inv-count').text(count || 0);
    }

    // ── 退回模式 ─────────────────────────────────────────
    var returnMode = false;

    function toggleReturnMode() {
        returnMode = !returnMode;
        var $backpack = $('.wgp-backpack');
        $backpack.toggleClass('wgp-return-mode', returnMode);
        $backpack.find('.wgp-return-mode-btn').toggleClass('active', returnMode);
        $backpack.find('.wgp-return-all-btn').toggle(returnMode);

        if (!returnMode) {
            $backpack.find('.wgp-inventory-item').removeClass('wgp-return-checked');
            $backpack.find('.wgp-single-return-btn').remove();
            $('.wgp-batch-bar').remove();
        } else {
            // 為每張虛擬商品卡片加入單個退回按鈕
            $backpack.find('.wgp-inventory-item').each(function () {
                var $item = $(this);
                if ($item.data('prize-type') !== 'virtual') return;
                var pts = parseInt($item.data('return-points') || 0, 10);
                if (pts <= 0) return;
                var $btn = $(`<button class="wgp-single-return-btn" type="button" data-draw-log-id="${$item.data('draw-log-id')}">退回 ${pts} 券</button>`);
                if ($item.hasClass('equipped')) {
                    $btn.prop('disabled', true).attr('title', '請先卸除裝備');
                }
                $item.find('.wgp-inv-info').append($btn);
            });
            updateBatchBar();
        }
    }

    $(document).on('click', '.wgp-backpack .wgp-return-mode-btn', toggleReturnMode);

    function updateBatchBar() {
        if (!returnMode) { $('.wgp-batch-bar').remove(); return; }
        var $checked = $('.wgp-backpack .wgp-inventory-item.wgp-return-checked');
        var count = $checked.length;
        var totalPts = 0;
        $checked.each(function () { totalPts += parseInt($(this).data('return-points') || 0, 10); });

        var $bar = $('.wgp-batch-bar');
        if (count === 0) { $bar.remove(); return; }
        if (!$bar.length) {
            $bar = $('<div class="wgp-batch-bar"><span class="wgp-batch-info"></span><button class="wgp-batch-return-btn" type="button">批次退回</button></div>').appendTo('body');
        }
        $bar.find('.wgp-batch-info').text(`已選 ${count} 件，共 ${totalPts.toLocaleString()} 玩瘋券`);
    }

    // 一鍵退回
    $(document).on('click', '.wgp-backpack .wgp-return-all-btn', function () {
        var ids = [];
        var totalPts = 0;
        $('.wgp-backpack .wgp-inventory-item:visible').each(function () {
            var $item = $(this);
            if ($item.hasClass('equipped')) return;
            if ($item.data('prize-type') !== 'virtual') return;
            var pts = parseInt($item.data('return-points') || 0, 10);
            if (pts > 0 && pts <= 30) {
                ids.push($item.data('draw-log-id'));
                totalPts += pts;
            }
        });
        if (!ids.length) { alert('沒有可一鍵退回的裝備（≤30 點、非穿戴中）'); return; }
        if (!confirm(`確定一鍵退回 ${ids.length} 件裝備，共 ${totalPts.toLocaleString()} 玩瘋券？\n退回後裝備將永久消失。`)) return;
        doBatchReturn(ids);
    });

    // 批次退回按鈕
    $(document).on('click', '.wgp-batch-return-btn', function () {
        var ids = [];
        $('.wgp-backpack .wgp-inventory-item.wgp-return-checked').each(function () {
            ids.push($(this).data('draw-log-id'));
        });
        if (!ids.length) return;
        if (!confirm(`確定退回 ${ids.length} 件裝備？退回後裝備將永久消失。`)) return;
        doBatchReturn(ids);
    });

    function doBatchReturn(ids) {
        restClient.post('/gacha/doll/virtual-return/batch', { draw_log_ids: ids })
        .then(function (res) {
            if (res.success) {
                alert(`已成功退回 ${res.data.returned_count} 件，共 ${res.data.total_points.toLocaleString()} 玩瘋券！`);
                // 先計算退回後的背包數量，避免 fadeOut 動畫未完成時計算錯誤
                var currentCount = $('.wgp-backpack .wgp-inventory-item:visible').length;
                var remaining = currentCount - res.data.returned_count;
                ids.forEach(function (id) {
                    if (res.data.failed_ids && res.data.failed_ids.indexOf(id) !== -1) return;
                    var $item = $(`.wgp-inventory-item[data-draw-log-id="${id}"]`);
                    $item.fadeOut(300, function () { $(this).remove(); });
                });
                updateBatchBar();
                // 更新背包數量（使用退回前快照減去退回數，不受 fadeOut 動畫時序影響）
                $('.wgp-inv-count').text(remaining);
            } else {
                alert(`退回失敗：${(res.data && res.data.message) || '未知錯誤'}`);
            }
        })
        .catch(function () { alert('連線錯誤，請稍後再試'); });
    }

    // ── 單個退回按鈕 ─────────────────────────────────────
    $(document).on('click', '.wgp-single-return-btn', function (e) {
        e.stopPropagation();
        var $btn = $(this);
        var $item = $btn.closest('.wgp-inventory-item');
        var drawLogId = $btn.data('draw-log-id');
        var pts = parseInt($item.data('return-points') || 0, 10);
        if (!confirm(`確定退回此裝備（${pts} 玩瘋券）？退回後裝備將永久消失。`)) return;
        $btn.prop('disabled', true);
        restClient.post('/gacha/doll/virtual-return', { draw_log_id: drawLogId })
        .then(function (res) {
            if (res.success) {
                $item.fadeOut(300, function () { $(this).remove(); });
                var remaining = $('.wgp-backpack .wgp-inventory-item:visible').length - 1;
                $('.wgp-inv-count').text(Math.max(0, remaining));
            } else {
                alert(`退回失敗：${(res.data && res.data.message) || '未知錯誤'}`);
                $btn.prop('disabled', false);
            }
        })
        .catch(function () {
            alert('連線錯誤，請稍後再試');
            $btn.prop('disabled', false);
        });
    });

    // ── Phase 3：穿上裝備（點背包 item）───────────────────
    $(document).on('click', '.wgp-inventory-item', function () {
        var $item       = $(this);

        // 退回模式下的行為
        if (returnMode) {
            if ($item.hasClass('equipped')) {
                alert('請先卸除裝備再退回');
                return;
            }
            if ($item.data('prize-type') !== 'virtual') {
                alert('僅虛擬裝備可退回');
                return;
            }
            if (!$item.hasClass('wgp-return-checked') &&
                $('.wgp-backpack .wgp-inventory-item.wgp-return-checked').length >= 50) {
                alert('每次最多只能選 50 件');
                return;
            }
            $item.toggleClass('wgp-return-checked');
            updateBatchBar();
            return;
        }

        var inventoryId = $item.data('inventory-id');

        restClient.post('/gacha/doll/equip', { inventory_id: inventoryId })
        .then(function (res) {
            if (!res.success) {
                alert((res.data && res.data.message) || '裝備失敗');
                return;
            }
            // 更新疊圖
            updateLayerImage(res.data.slot, res.data.layer_image_url);
            // 更新部位縮圖 + boss rate
            updateSlotThumb(res.data.slot, res.data.inventory_image_url, inventoryId, res.data.boss_reward_rate, res.data.rarity);
            $(`.wgp-slot-cell[data-slot="${res.data.slot}"]`).attr('data-boss-rate', res.data.boss_reward_rate || 0);
            // 若有互斥部位需清空
            if (res.data.cleared_slots && res.data.cleared_slots.length) {
                res.data.cleared_slots.forEach(function (s) {
                    clearLayerImage(s);
                    clearSlotThumb(s);
                    $(`.wgp-slot-cell[data-slot="${s}"]`).attr('data-boss-rate', 0);
                });
            }
            updateBossRateDisplay();
            // 視覺標示：降格同部位舊穿戴
            var $grid = $('.wgp-inventory-grid');
            $grid.find(`.wgp-inventory-item[data-slot="${res.data.slot}"].equipped`).each(function () {
                $(this).removeClass('equipped').find('.wgp-inv-equipped-label').remove();
                $grid.append($(this));
            });
            // 標記新穿戴
            $item.addClass('equipped');
            if (!$item.find('.wgp-inv-equipped-label').length) {
                $item.find('.wgp-inv-slot').after('<span class="wgp-inv-equipped-label">穿戴中</span>');
            }
            $grid.prepend($item);
            // 同步紙娃娃外觀給在線玩家
            if (window._wtoyUpdateDollPresence) window._wtoyUpdateDollPresence();
        })
        .catch(function () {
            alert('連線錯誤');
        });
    });

    // ── Phase 3：卸除裝備（點裝備槽）─────────────────────
    $(document).on('click', '.wgp-slot-cell.equipped', function () {
        var slot = $(this).data('slot');

        restClient.post('/gacha/doll/unequip', { slot: slot })
        .then(function (res) {
            if (!res.success) {
                return;
            }
            clearLayerImage(res.data.slot);
            clearSlotThumb(res.data.slot);
            $(`.wgp-slot-cell[data-slot="${res.data.slot}"]`).attr('data-boss-rate', 0);
            // 清除背包 item 的穿戴視覺
            var $item = $(`.wgp-inventory-item[data-slot="${res.data.slot}"].equipped`);
            $item.removeClass('equipped').find('.wgp-inv-equipped-label').remove();
            $('.wgp-inventory-grid').append($item);
            updateBossRateDisplay();
            // 同步紙娃娃外觀給在線玩家
            if (window._wtoyUpdateDollPresence) window._wtoyUpdateDollPresence();
        });
    });

    // ── 即時更新總收益率顯示（僅虛擬轉蛋由客戶端計算）──────
    function updateBossRateDisplay() {
        if (gachaProductType !== 'virtual') return;
        var total = 0;
        $('.wgp-slot-cell').each(function () {
            total += parseInt($(this).attr('data-boss-rate') || 0, 10);
        });
        var pct = total / 100;
        // wgp-boss-hint（角色名稱下方）
        var $hint = $('.wgp-boss-hint');
        if (total > 0) {
            $hint.html(`🎖 總收益：${pct}%`);
        } else {
            $hint.html('💡 穿上黑鑽款服裝才能獲得主宰收益！');
        }
        // wgp-doll-boss-badge（角色上方）
        var $badge = $('.wgp-doll-boss-badge');
        if ($badge.length && total > 0) {
            $badge.html(`👑 幕後主宰 ${pct}% 收益`).show();
        } else if ($badge.length) {
            $badge.hide();
        }
    }

    // ── DOM 操作輔助 ───────────────────────────────────────
    function updateLayerImage(slot, url) {
        var $layer = $(`.wgp-layer[data-slot="${slot}"]`);
        if (url) {
            $layer.attr('src', url).show();
        } else {
            $layer.attr('src', '').hide();
        }
    }

    function clearLayerImage(slot) {
        updateLayerImage(slot, '');
    }

    function updateSlotThumb(slot, url, inventoryId, bossRate, rarity) {
        var $cell = $(`.wgp-slot-cell[data-slot="${slot}"]`);
        $cell.addClass('equipped');
        // 更新稀有度 class
        $cell[0].className = $cell[0].className.replace(/\bwgp-rarity-\d+\b/g, '');
        if (rarity) {
            $cell.addClass(`wgp-rarity-${rarity}`);
            $cell.attr('data-rarity', rarity);
        }
        var html = url ? `<img src="${escAttr(url)}" alt="">` : '';
        if (bossRate > 0) {
            html += `<span class="wgp-inv-rate-badge">${escHtml(`${bossRate / 100}%`)}</span>`;
        }
        $cell.html(html);
        if (inventoryId !== undefined) {
            $cell.data('inventory-id', inventoryId);
        }
    }

    function clearSlotThumb(slot) {
        var $cell = $(`.wgp-slot-cell[data-slot="${slot}"]`);
        $cell.removeClass('equipped').removeData('inventory-id');
        $cell[0].className = $cell[0].className.replace(/\bwgp-rarity-\d+\b/g, '');
        $cell.removeAttr('data-rarity');
        // 還原為 label 文字（從 data-slot 推斷 label）
        var labels = {
            head: '頭部', left_hand: '左手', right_hand: '右手',
            both_hands: '雙手', top: '上衣', bottom: '下身',
            shoes: '鞋子', background: '背景', pet: '寵物'
        };
        $cell.html(`<span class="wgp-slot-label">${labels[slot] || slot}</span>`);
    }

    // ── Ably 即時更新 boss banner ─────────────────────────
    $(document).ready(function () {
        drawHoldPopup = mountDrawHoldPopup({
            root: document.getElementById('wtoy_draw_hold_popup'),
            restBase: restBase,
            nonce: nonce,
            onCancelled: function () {
                location.reload();
            },
            // 查到待付單已付款（金流回來後 pending 單狀態已翻）：接手既有的回流輪詢
            // 收斂（與 handleReserve() 成功後導去金流回來走的是同一支）
            onPoll: function (orderId) {
                drawPayPopup.startReturnPollingForOrder(orderId);
            },
        });

        // 跳轉付現／當頁刷卡回導後的全新頁面載入：查 URL / sessionStorage 判斷
        // 有沒有進行中的付款單，有則接手輪詢
        drawPayPopup.initReturnPolling();

        if (window.ably && productId) {
            var bossChannel = window.ably.channels.get(`product:${productId}`);

            bossChannel.subscribe('boss_changed', function (msg) {
                var d = msg.data;
                var name = (userToken && d.user_token === userToken) ? '我' : d.display_name;
                updateBossBanner(name, d.accumulated_revenue, d.effective_rate);
            });

            bossChannel.subscribe('boss_rate_updated', function (msg) {
                updateBossBannerRate(msg.data.effective_rate);
            });

            bossChannel.subscribe('boss_revenue_updated', function (msg) {
                $('.wgp-boss-revenue-amount').text(`$${Number(msg.data.accumulated_revenue).toLocaleString()}`);
            });

            bossChannel.subscribe('gacha_quota_updated', function (msg) {
                var d = msg.data || {};
                var total = parseInt(d.total_remaining, 10) || 0;

                $('[data-quota-remaining]').text(total.toLocaleString());
                $('.wgp-prize-card[data-is-limited="1"]').attr('data-total-remaining', total);

                // 更新底部 draw bar 進度條
                var $progress = $('[data-quota-progress]');
                if ($progress.length) {
                    var grandTotal = parseInt(($('.wgp-draw-summary-total').text() || '0').replace(/\D/g, ''), 10) || 0;
                    var pct = grandTotal > 0 ? Math.max(0, Math.min(100, (total / grandTotal) * 100)) : 0;
                    $progress.css('--quota-pct', pct + '%');
                }

                // limited 模式：抽獎列上限跟著剩餘配額重算
                if ($('.wgp-draw-bar--limited').length) {
                    drawLimits.quotaRemaining = total;
                    applyDrawLimits();
                }

                (d.prizes || []).forEach(function (p) {
                    var $card = $('.wgp-prize-card[data-prize-id="' + p.id + '"]');
                    if (!$card.length) return;
                    var remaining = parseInt(p.remaining_quota, 10) || 0;
                    var quota     = parseInt(p.quota, 10) || 0;
                    var pct       = quota > 0 ? Math.max(0, Math.min(100, (remaining / quota) * 100)) : 0;

                    $card.attr('data-remaining', remaining);
                    $card.find('.wgp-prize-quota-num').text(remaining);
                    $card.find('.wgp-prize-progress-fill').css('width', pct + '%');
                    $card.toggleClass('wgp-prize-card--sold-out', remaining <= 0);
                });
            });

            bossChannel.subscribe('gacha_delisted', function () {
                // 之後的配額推播重算上限時，不會再把按鈕打開
                drawLimits.blocked = true;
                $('.wgp-draw-bar').addClass('is-ineligible');
                $('.wgp-draw-btn, .wgp-draw-custom-btn').prop('disabled', true).css('opacity', 0.6);
                if (!document.querySelector('.wgp-delisted-banner')) {
                    $('body').append('<div class="wgp-delisted-banner" style="position:fixed;top:60px;left:50%;transform:translateX(-50%);background:#dc2626;color:#fff;padding:10px 20px;border-radius:8px;font-weight:700;z-index:99999;box-shadow:0 4px 12px rgba(0,0,0,0.3);">此商品已下架</div>');
                }
            });

            bossChannel.subscribe('gacha_delisting_time_updated', function () {
                location.reload();
            });

            // gacha_drawn：整塊重撈 winners list（含 pinned 區），避免 pinned/body 排序衝突
            bossChannel.subscribe('gacha_drawn', function () {
                clearTimeout(window._wgpWinnersTimer);
                window._wgpWinnersTimer = setTimeout(refreshWinnersList, 300);
            });

            // ── 在線玩家紙娃娃 (Ably Presence) ────────────────────
            // presence 只用來決定「誰在線」（clientId 由 token 綁定），名稱與圖片一律
            // 由 /gacha/doll/presence 反查，前端不送、也不採信任何顯示用 payload。
            var myClientId = window.WTOY_ABLY_SETTING ? window.WTOY_ABLY_SETTING.currentUserId : '';
            var onlineDollsTimer = null;

            // 未登入者的 token 沒有 clientId，進不了 presence。不呼叫 enter，
            // 只保留 subscribe 與 get，仍看得到其他人的紙娃娃與在線人數。
            if (myClientId) {
                bossChannel.presence.enter().catch(function (err) {
                    console.warn('[wtoy] presence enter failed', err);
                });
            }

            function refreshOnlineDolls() {
                bossChannel.presence.get().then(function (members) {
                    var unique = dedupeByClientId(members);
                    // 計數在截斷渲染筆數之前完成，口徑 = 不重複 clientId 總數
                    $('#wgp-online-dolls-count').text(unique.length);

                    var clientIds = renderableClientIds(unique, myClientId);
                    fetchDollCards(publicRestClient, clientIds).then(function (cards) {
                        $('#wgp-online-dolls-row').html(onlineDollsHtml(clientIds, cards, myClientId));
                    });
                });
            }

            // presence 事件會連續進來（多人同時進出、自己更新也會回音），合併成一次查詢
            function scheduleOnlineDollsRefresh() {
                clearTimeout(onlineDollsTimer);
                onlineDollsTimer = setTimeout(refreshOnlineDolls, 300);
            }

            bossChannel.presence.subscribe('enter', scheduleOnlineDollsRefresh);
            bossChannel.presence.subscribe('leave', scheduleOnlineDollsRefresh);
            bossChannel.presence.subscribe('update', scheduleOnlineDollsRefresh);
            refreshOnlineDolls();

            // 裝備/卸除後通知其他玩家重抓：外觀已存在伺服器端，這裡只送變更訊號
            window._wtoyUpdateDollPresence = function () {
                bossChannel.presence.update().catch(function (err) {
                    console.warn('[wtoy] presence update failed', err);
                });
                scheduleOnlineDollsRefresh();
            };
        }
    });

    function updateBossBanner(name, revenue, effectiveRate) {
        $('.wgp-boss-name').text(name);
        $('.wgp-boss-revenue-amount').text(`$${Number(revenue).toLocaleString()}`);
        updateBossBannerRate(effectiveRate);
    }

    function updateBossBannerRate(effectiveRate) {
        var pct = effectiveRate / 100;
        var $badge = $('.wgp-boss-rate-badge');
        if (effectiveRate > 0) {
            if ($badge.length) {
                $badge.text(`${pct}% 收益`);
            } else {
                $('.wgp-boss-title').append(` <span class="wgp-boss-rate-badge">${pct}% 收益</span>`);
            }
        } else {
            $badge.remove();
        }
    }

    // ── 中獎名單：整塊重撈（pinned + body 一起換） ───────────────
    var winnersRefreshPending = false;
    function refreshWinnersList() {
        if (winnersRefreshPending || !productId) return;
        winnersRefreshPending = true;
        publicRestClient.get('/gacha/winners', { product_id: productId })
            .then(function (res) {
                if (res && res.success && res.data && res.data.html) {
                    $('.wgp-winners-list').replaceWith(res.data.html);
                }
            })
            .finally(function () { winnersRefreshPending = false; });
    }

    // ── 手機版抽屜開關 ─────────────────────────────────────
    $('.wgp-drawer-toggle, .wgp-drawer-backdrop').on('click', function () {
        $('.wgp-right').toggleClass('wgp-drawer-open');
        $('.wgp-drawer-backdrop').toggleClass('active');
        $('.wgp-drawer-toggle').toggleClass('active');
    });

    // 向右滑動關閉抽屜
    (function () {
        var startX = 0;
        var startY = 0;
        var tracking = false;

        $(document).on('touchstart', '.wgp-right', function (e) {
            var touch = e.originalEvent.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            tracking = true;
        });

        $(document).on('touchmove', '.wgp-right', function (e) {
            if (!tracking) return;
            var touch = e.originalEvent.touches[0];
            var dx = touch.clientX - startX;
            var dy = touch.clientY - startY;
            // 只在水平滑動明顯大於垂直時才攔截
            if (dx > 30 && Math.abs(dy) < dx) {
                e.preventDefault();
            }
        });

        $(document).on('touchend', '.wgp-right', function (e) {
            if (!tracking) return;
            tracking = false;
            var touch = e.originalEvent.changedTouches[0];
            var dx = touch.clientX - startX;
            if (dx > 60) {
                $('.wgp-right').removeClass('wgp-drawer-open');
                $('.wgp-drawer-backdrop').removeClass('active');
                $('.wgp-drawer-toggle').removeClass('active');
            }
        });
    })();

    // ── 工具 ───────────────────────────────────────────────
    function escHtml(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function escAttr(str) {
        return escHtml(str);
    }

    // ── 瞬殺模式：已下架時禁用抽獎按鈕 ────────────────────────
    if (cfg.gacha_mode === 'limited' && parseInt(cfg.delisting_time, 10) > 0) {
        var nowSec = Math.floor(Date.now() / 1000);
        if (nowSec >= parseInt(cfg.delisting_time, 10)) {
            drawLimits.blocked = true;
            $('.wgp-draw-btn, .wgp-draw-custom-btn').prop('disabled', true).css('opacity', 0.5);
        }
    }

    // ── 瞬殺模式：抽獎後即時更新剩餘配額顯示 ──────────────────
    function updateLimitedQuotaDisplay(results) {
        if (!results || !results.length) return;
        var totalDecrement = results.length;
        var $remTotal = $('.wgp-quota-remaining[data-quota-remaining]');
        if ($remTotal.length) {
            var cur = parseInt($remTotal.text().replace(/\D/g, ''), 10) || 0;
            $remTotal.text(Math.max(0, cur - totalDecrement).toLocaleString());
        }
        // 各稀有度小計：依抽中結果分組減少
        var decByRarity = {};
        results.forEach(function (r) {
            decByRarity[r.rarity] = (decByRarity[r.rarity] || 0) + 1;
        });
        Object.keys(decByRarity).forEach(function (rarity) {
            var $section = $('.wgp-rarity-section[data-rarity="' + rarity + '"]');
            if (!$section.length) return;
            var $rq = $section.find('.wgp-rarity-quota');
            if ($rq.length) {
                // 文字格式："剩餘 N / M"，只改 N
                var text = $rq.text();
                var m = text.match(/(\d+)\s*\/\s*(\d+)/);
                if (m) {
                    var newRem = Math.max(0, parseInt(m[1], 10) - decByRarity[rarity]);
                    $rq.text('剩餘 ' + newRem + ' / ' + m[2]);
                }
            }
        });
    }

    // ── 瞬殺模式：下架倒數計時 ────────────────────────────────
    if (cfg.gacha_mode === 'limited' && parseInt(cfg.delisting_time, 10) > 0) {
        var $countdownEl = $('.wgp-delisting-countdown');
        if ($countdownEl.length) {
            var deadline = parseInt(cfg.delisting_time, 10);
            var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
            var renderCountdown = function () {
                var now = Math.floor(Date.now() / 1000);
                var diff = deadline - now;
                if (diff <= 0) {
                    // 過期：reload 顯示已下架狀態
                    location.reload();
                    return;
                }
                var h = Math.floor(diff / 3600);
                var m = Math.floor((diff % 3600) / 60);
                var s = diff % 60;
                $countdownEl.text(pad(h) + ':' + pad(m) + ':' + pad(s));
            };
            renderCountdown();
            setInterval(renderCountdown, 1000);
        }
    }

})(jQuery);
